import React, {useEffect, useRef, useState} from 'react';

import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';

import buildInteractiveFlowchartFromStaticData from '../buildInteractiveFlowchartFromStaticData';
import {FlowchartEdge, StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';

interface UseInteractiveFlowchartBuilderParams {
  staticFlowchart: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  nodePositions: Record<string, {x: number; y: number}>;
  currentNodePositionsRef: React.MutableRefObject<Record<string, {x: number; y: number}>>;
  interactiveFlowchartOptions: FlowchartOptions;
  validationErrors: Array<ProcessFlowchartValidationError>;
  setInteractiveNodes: React.Dispatch<React.SetStateAction<Array<FlowchartNode>>>;
  setInteractiveEdges: React.Dispatch<React.SetStateAction<Array<FlowchartEdge>>>;
}

export interface InteractiveFlowchartBuilderResult {
  elkEdgeDataByEdgeId: Record<string, ElkEdgeData>;
  setElkEdgeDataByEdgeId: React.Dispatch<React.SetStateAction<Record<string, ElkEdgeData>>>;
}

/**
 * Rebuilds the interactive nodes + edges whenever static state or selection
 * changes. `nodePositions` is mirrored into a ref and read from there inside
 * the effect so position-only changes don't trigger full rebuilds (which would
 * thrash during drag operations); the ref is still read for newly-added nodes.
 */
export function useInteractiveFlowchartBuilder({
  staticFlowchart,
  flowchartConfigMetadata,
  nodePositions,
  currentNodePositionsRef,
  interactiveFlowchartOptions,
  validationErrors,
  setInteractiveNodes,
  setInteractiveEdges,
}: UseInteractiveFlowchartBuilderParams): InteractiveFlowchartBuilderResult {
  const [elkEdgeDataByEdgeId, setElkEdgeDataByEdgeId] = useState<Record<string, ElkEdgeData>>({});

  // Mirror `nodePositions` into a ref so the rebuild effect can read the latest
  // positions without subscribing to position-only changes (which would trigger
  // a full rebuild on every drag tick).
  const nodePositionsRef = useRef(nodePositions);
  nodePositionsRef.current = nodePositions;

  useEffect(() => {
    if (!staticFlowchart) {
      return;
    }

    // Use current positions from ref to preserve drag positions; falls back to
    // the latest committed state for nodes that don't exist yet (e.g., newly added).
    const positionsWithFallback = {
      ...nodePositionsRef.current,
      ...currentNodePositionsRef.current,
    };

    const {nodes, edges} = buildInteractiveFlowchartFromStaticData({
      staticNodesData: staticFlowchart.nodesStaticData,
      staticEdgesData: staticFlowchart.edgesStaticData,
      options: interactiveFlowchartOptions,
      nodePositionsByNodeId: positionsWithFallback,
      selectedNodeIds: flowchartConfigMetadata.interactionState.selectedNodeIds,
      selectedRowIds: flowchartConfigMetadata.interactionState.selectedRowIds,
      selectedEdgeIds: flowchartConfigMetadata.interactionState.selectedEdgeIds,
      elkEdgeDataByEdgeId,
      validationErrors,
    });

    nodes.forEach((node) => {
      currentNodePositionsRef.current[node.id] = node.position;
    });

    setInteractiveNodes(nodes);
    setInteractiveEdges(edges);
  }, [
    staticFlowchart,
    flowchartConfigMetadata.interactionState.selectedNodeIds,
    flowchartConfigMetadata.interactionState.selectedRowIds,
    flowchartConfigMetadata.interactionState.selectedEdgeIds,
    interactiveFlowchartOptions,
    elkEdgeDataByEdgeId,
    validationErrors,
    currentNodePositionsRef,
    setInteractiveNodes,
    setInteractiveEdges,
  ]);

  return {elkEdgeDataByEdgeId, setElkEdgeDataByEdgeId};
}
